import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import PortalLayout from '../../components/PortalLayout';
import { getCustomerTicket, addCustomerTicketComment, errorMessage } from '../../services/customerApi';
import { useLanguage } from '../../i18n/LanguageContext';
import { fmtDateTime } from '../../utils/format';
import { Pill, STATUS_COLORS } from '../../components/NotionUI';

function PortalTicketDetail({ customer, onLogout }) {
  const { t } = useLanguage();
  const { id } = useParams();
  const [ticket, setTicket] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [replyBody, setReplyBody] = useState('');
  const [sending, setSending] = useState(false);

  const fetchTicket = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getCustomerTicket(id);
      setTicket(data.ticket);
      setComments(data.comments || []);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchTicket();
  }, [fetchTicket]);

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyBody.trim()) return;
    setSending(true);
    setError('');
    try {
      const res = await addCustomerTicketComment(id, replyBody.trim());
      setComments(res.comments || []);
      setReplyBody('');
    } catch (err) {
      setError(errorMessage(err, t('portal.replyError')));
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <PortalLayout companyName={t('app.title')} onLogout={onLogout}>
        <div className="loading">{t('common.loading')}</div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout title={ticket ? t('portal.trackTitle', { id: ticket.id }) : undefined} companyName={t('app.title')} onLogout={onLogout}>
      <Link to="/portal/dashboard" className="portal-back">
        ← {t('portal.backToDashboard')}
      </Link>
      {error && <div className="error-message">{error}</div>}
      {ticket && (
        <>
          <div className="card portal-gap">
            <div className="card-body">
              <div className="portal-ticket-head">
                <Pill dot color={STATUS_COLORS[ticket.status]}>{t(`portal.status_${ticket.status}`)}</Pill>
                <span className="portal-muted">{fmtDateTime(ticket.created_at)}</span>
              </div>
              <h2 className="portal-ticket-title">{ticket.subject}</h2>
              <p className="portal-ticket-desc">{ticket.description}</p>
            </div>
          </div>

          <div className="portal-thread">
            {comments.map((c) => (
              <div key={c.id} className={'portal-msg' + (c.author_type === 'customer' ? ' portal-msg-mine' : '')}>
                <div className="portal-bubble">
                  <div className="portal-bubble-head">
                    <strong>{c.author_type === 'customer' ? (customer?.name || t('portal.email')) : (c.agent_name || 'Staff')}</strong>
                    <span>{fmtDateTime(c.created_at)}</span>
                  </div>
                  <div className="portal-bubble-body">{c.body}</div>
                </div>
              </div>
            ))}
          </div>

          <form onSubmit={handleReply} className="card">
            <div className="card-body">
              <div className="form-group">
                <textarea rows={3} value={replyBody} onChange={(e) => setReplyBody(e.target.value)} placeholder={t('portal.replyPlaceholder')} />
              </div>
              <button type="submit" className="btn" disabled={sending || !replyBody.trim()}>
                {sending ? t('portal.sending') : t('portal.send')}
              </button>
            </div>
          </form>
        </>
      )}
    </PortalLayout>
  );
}

export default PortalTicketDetail;
